/* eslint-disable max-lines-per-function */
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Chip, FlexSpan, Icon, Input, NumberInput, DatePicker, Tooltip, AutoComplete } from "@evergis/ui";
import { Flex } from "grid-styled";
import { useTranslation } from "react-i18next";

import {
  AttributeType,
  QueryLayerServiceConfigurationDc,
  QueryLayerServiceEqlParameterConfigurationDc,
} from "@evergis/api";

import {
  EqlParamsContainer,
  EqlParamsInfo,
  EqlParamsList,
  EqlParamsShowButton,
  EqlParamsTitle,
  EqlParamsWrapper,
  EqlTooltipContent,
} from "../styled";

import { useToggle } from "../../../hooks/useToggle";
import { useLayerParam } from "../../../hooks/eql/useLayerParam";
import { useRefreshLayer } from "../../../hooks/layer/useRefreshLayer";
import { DateFormat, formatDate, getDate } from "../../../utils/date";

import { LayerInfo } from "../../../interfaces/resourceInfo";

const NUMBER_TYPES = [AttributeType.Double, AttributeType.Int32, AttributeType.Int64];
const STRING_TYPES = [AttributeType.String];
const DATETIME_TYPES = [AttributeType.DateTime];
const ALLOWED_TYPES = [...NUMBER_TYPES, ...STRING_TYPES, ...DATETIME_TYPES];

export const EqlParams: FC<{
  layer?: LayerInfo;
  tempParams?: Record<string, string>;
  setTempParams?: (values: Record<string, string>) => void;
}> = ({ layer, tempParams, setTempParams }) => {
  const { t } = useTranslation("layerFilter");
  const [isOpen, toggleIsOpen] = useToggle(true);
  const params = useMemo(() => (layer.configuration as QueryLayerServiceConfigurationDc).eqlParameters, [layer]);
  const [momentParams, setMomentParams] = useState<Record<string, string>>();
  const filtersCount = useMemo(() => (params ? Object.keys(params).length : 0), [params]);
  const [, setLayerParam] = useLayerParam();
  const refreshLayer = useRefreshLayer();

  const onUpdateParam = useCallback(
    async (key: string, value: string) => {
      if (!layer) {
        return;
      }

      await setLayerParam({
        input: {
          layerName: layer.name,
          paramName: key,
          value,
        },
      });

      refreshLayer(layer.name);
    },
    [layer, refreshLayer, setLayerParam],
  );

  const renderParamField = useCallback(
    (key: string, values: QueryLayerServiceEqlParameterConfigurationDc) => {
      if (tempParams === undefined) {
        return null;
      }

      const inputLabel = values.alias || key;

      if (!values.availableValues) {
        if (NUMBER_TYPES.includes(values.type)) {
          return (
            <NumberInput
              label={inputLabel}
              width="280px"
              value={Number(tempParams[key])}
              onChange={e => {
                setTempParams({
                  ...tempParams,
                  [key]: e !== null ? e.toString() : "",
                });
              }}
              onBlur={e => {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                if (e.target.rawValue !== momentParams[key]) {
                  onUpdateParam(key, tempParams[key]).then(() => setMomentParams(tempParams));
                }
              }}
            />
          );
        }

        if (STRING_TYPES.includes(values.type)) {
          return (
            <Input
              label={inputLabel}
              width="280px"
              value={tempParams[key]}
              onChange={e => {
                setTempParams({
                  ...tempParams,
                  [key]: e.target.value,
                });
              }}
              onBlur={e => {
                if (e.target.value !== momentParams[key]) {
                  onUpdateParam(key, tempParams[key]).then(() => setMomentParams(tempParams));
                }
              }}
            />
          );
        }

        if (DATETIME_TYPES.includes(values.type)) {
          return (
            <DatePicker
              withTime
              label={inputLabel}
              width="280px"
              value={getDate(tempParams[key])}
              onChange={async e => {
                const newValue = formatDate(e, { dateFormat: DateFormat.UTC });

                if (newValue !== tempParams[key]) {
                  const newTempParams = {
                    ...tempParams,
                    [key]: newValue,
                  };

                  onUpdateParam(key, newValue).then(() => {
                    setTempParams(newTempParams);
                    setMomentParams(newTempParams);
                  });
                }
              }}
              zIndex={100}
            />
          );
        }
      } else {
        return (
          <AutoComplete
            label={inputLabel}
            value={tempParams[key]?.toString()}
            menuWidth="auto"
            options={values.availableValues.map(value => ({
              text: value?.toString() ?? "",
              value: value?.toString() ?? "",
            }))}
            onChange={value => {
              setTempParams({
                ...tempParams,
                [key]: value,
              });
            }}
            onBlur={() => {
              const newValue = tempParams[key] || "";

              onUpdateParam(key, newValue).then(() => {
                setMomentParams({
                  ...tempParams,
                  [key]: newValue,
                });
              });
            }}
            onSelect={options => {
              const newValue = options[0]?.value || "";

              onUpdateParam(key, newValue).then(() => {
                setMomentParams({
                  ...tempParams,
                  [key]: newValue,
                });
              });
            }}
            zIndex={100}
            width="280px"
          />
        );
      }

      return null;
    },
    [onUpdateParam, setTempParams, tempParams, momentParams],
  );

  useEffect(() => {
    if (momentParams === undefined) {
      setMomentParams(tempParams);
    }
  }, [tempParams]); // eslint-disable-line

  if (!layer || filtersCount === 0) {
    return null;
  }

  return (
    <EqlParamsWrapper>
      <EqlParamsContainer>
        <FlexSpan style={{ width: "100%" }}>
          <Flex alignItems="baseline" style={{ width: "100%", gap: "1rem" }}>
            <FlexSpan style={{ flexGrow: 1 }}>
              <Flex alignItems="center" style={{ gap: "0.5rem" }}>
                <FlexSpan>
                  <EqlParamsTitle>{t("systemFilters")}</EqlParamsTitle>
                </FlexSpan>
                {filtersCount && (
                  <FlexSpan>
                    <Chip text={filtersCount.toString()} />
                  </FlexSpan>
                )}
              </Flex>
            </FlexSpan>
            <FlexSpan>
              <Tooltip
                arrow
                placement="top"
                content={<EqlTooltipContent>{t("filtersTooltip")}</EqlTooltipContent>}
                delay={[300, 0]}
                maxWidth="10rem"
              >
                {ref => <EqlParamsInfo ref={ref} kind="info" />}
              </Tooltip>
            </FlexSpan>
            <FlexSpan>
              <EqlParamsShowButton $isOpen={isOpen} onClick={toggleIsOpen}>
                <Icon kind="expand" />
              </EqlParamsShowButton>
            </FlexSpan>
          </Flex>
        </FlexSpan>
        {isOpen && (
          <FlexSpan>
            <EqlParamsList>
              {Object.entries(params).reduce((acc, [key, values], currIndex) => {
                if (!ALLOWED_TYPES.includes(values.type)) {
                  return acc;
                }

                return [...acc, <FlexSpan key={`eql-param-${currIndex}`}>{renderParamField(key, values)}</FlexSpan>];
              }, [])}
            </EqlParamsList>
          </FlexSpan>
        )}
      </EqlParamsContainer>
    </EqlParamsWrapper>
  );
};
